import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../components/MainLayout';
import { apiUrls } from '../utils/apiUrls';
import { callAPI } from '../utils/apiUtils';
import { HTTP_STATUS } from "../utils/httpStatusCodes";
import { useLocation, useNavigate } from 'react-router-dom';
import GuardedNavigationModal from '../components/modals/GuardedNavigationModal';
import useNavigationGuard from '../hooks/useNavigationGuard'
import { toast } from 'react-toastify';
import '../styles/autocomplete.css';
import { useConnection } from '../offline/connection';
import { canDo } from '../offline/state';
import ScanButton from '../components/ScanButton';
import { MEMBER_EVENT, memberLabel, takePendingMember } from '../utils/memberScan';


const SelectCustomer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Offline, finding an existing customer needs the cloud (only brand-new customers can be saved on the device).
  const { state: offlineState } = useConnection();
  const canSearchCustomers = canDo(offlineState, 'customerSearch');
  const [customer, setCustomer] = useState({
    first_name: '',
    last_name: '',
    mail: '',
    number: '',
    subscribed: false,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [orderType, setOrderType] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [orderIdNummeric, setOrderIdNumeric] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const suggestionBoxRef = useRef(null);
  const { showModal, confirmNavigation, cancelNavigation, guardedNavigate } = useNavigationGuard(true);
  useEffect(() => {
    const orderId = location.state?.order_id || localStorage.getItem('current_order_id');

    if (!orderId) {
      console.error('Missing order ID in location state.');
      navigate('/dashboard');
      return;
    }
  
    const fetchOrderInfo = async () => {
      try {
        const response = await callAPI(apiUrls.GET_ORDER_INFO, { order_id: orderId }, 'GET');
  
        if (response.status === HTTP_STATUS.OK && response.data.status === 1) {
          setOrderType(response.data.orderType);
          setOrderNumber(response.data.orderNumber);
          setOrderIdNumeric(response.data.orderDetail.id);
        } else {
          console.warn('Failed to fetch order info:', response.error || response);
        }
      } catch (err) {
        console.error('API error while fetching order info:', err);
      }
    };
  
    fetchOrderInfo();

    // Hide suggestions on outside click
    const handleClickOutside = (e) => {
      if (suggestionBoxRef.current && !suggestionBoxRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // A scanned membership card picks that customer, exactly like choosing them in the search box: the cashier then taps Next.
  const applyMember = (member) => {
    setCustomer({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      mail: member.email || '',
      number: member.contact_number || '',
      subscribed: false,
    });
    setSelectedCustomerId(member.customer_id);
    setSuggestions([]);
    setSearchQuery('');
    toast.success(`Member: ${memberLabel(member)}`);
  };

  useEffect(() => {
    const onMember = (e) => {
      const detail = e.detail || {};
      if (!detail.member || detail.handled) return;
      detail.handled = true;
      applyMember(detail.member);
    };
    window.addEventListener(MEMBER_EVENT, onMember);
    // a card scanned earlier, while the order was still being taken
    const waiting = takePendingMember();
    if (waiting) applyMember(waiting);
    return () => window.removeEventListener(MEMBER_EVENT, onMember);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setCustomer((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchSuggestions(value);
  };

  const fetchSuggestions = async (value) => {
    console.log(suggestions)
    if (!value || value.length < 2 || !canSearchCustomers) {
      setSuggestions([]);
      return;
    }
    //const urlAutoComplete = `${apiUrls.CUSTOMER_AUTOCOMPLETE}?field=first_name&term=${value}`;
    const urlAutoComplete = `${apiUrls.CUSTOMER_AUTOCOMPLETE}?term=${encodeURIComponent(value)}`;
    const response = await callAPI(urlAutoComplete, {}, 'GET');
    if (response.data.status === 1 && Array.isArray(response.data.data)) {
    
      setSuggestions(response.data.data);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    const customerData = suggestion.customerObject;

    setCustomer({
      first_name: customerData.firstName || '',
      last_name: customerData.lastName || '',
      mail: customerData.email || '',
      number: customerData.contactNumber || '',
      subscribed: false,
    });

    setSelectedCustomerId(customerData.id);
    setSuggestions([]);
    setSearchQuery('');
  };

  // 'take_away' => 1,
  // 'delivery'  => 2,
  // 'dine_in'   => 3,
  // 'table_order' => 4

  const continueAsGuest = () => {
    const customerId = selectedCustomerId || localStorage.getItem('current_customer_id');
    const orderId = orderNumber
  
    const navState = {
      customer_id: customerId,
      order_id: orderIdNummeric,
    };
    if (orderType == '2') {
      navigate('/add-delivery-address',{ state: navState });
    } else {
      navigate('/payment-view?order_id='+orderIdNummeric, { state: navState });
    }
  };

  const saveRecords = async () => {
    setErrors({});
    console.log(orderType)
    const payload = {
      ...customer,
      mobile: customer.number,
      name: `${customer.first_name} ${customer.last_name}`,
      order_id:orderNumber
    };

    if (!customer.first_name && !customer.last_name && !customer.number && orderType !== '2') {
      setErrors({ first_name: 'First name is required.' });
      return;
    }

    if (selectedCustomerId) {
      updateCustomerInOrder(selectedCustomerId);
    } else {
      const response = await callAPI(apiUrls.SAVE_CUSTOMER, {}, 'POST', payload, {});
      if (response.data.errors) {
        setErrors(response.data.errors);
        return;
      }

      // A phone number already belonging to another customer at this restaurant - the backend
      // (CustomerController::actionSaveCustomer) refuses to create a duplicate and answers 409
      // with no `errors` key, only a top-level `message`. Unhandled, this fell through both
      // branches below silently: not an error (no `errors` key), not a success (status is 409,
      // not 1) - Next just did nothing, with no indication why. AddSelectCustomerModal.js
      // already handles this same response the same way; mirrored here rather than invented.
      if (response.data.status === 409) {
        // The phone number belongs to an existing customer. Rather than dead-end here (or make
        // the user retype it into the search box above to get the same result), attach the
        // order to that customer directly - the same outcome handleSuggestionSelect already
        // gives, just reached from the other input. Falls back to the old error-only behaviour
        // if an older API build hasn't started returning the customer id yet.
        if (response.data.customer) {
          toast.info(response.data.message ? `${response.data.message} Continuing with their existing profile.` : 'A customer with this phone number already exists - continuing with their existing profile.');
          setSelectedCustomerId(response.data.customer);
          updateCustomerInOrder(response.data.customer);
          return;
        }

        setErrors({ number: response.data.message });
        return;
      }

      if (response.data.status === 1) {
        setSelectedCustomerId(response.data.customer);
        updateCustomerInOrder(response.data.customer);
      }
    }
  };

  const updateCustomerInOrder = async (customerId) => {
    const payload = {
      order_numbers: orderNumber,
      customer: customerId,
    };

    const response = await callAPI(apiUrls.UPDATE_ORDER_CUSTOMER, {}, 'POST', payload, {});
    if (response.status === HTTP_STATUS.OK ) {
      localStorage.setItem('current_customer_id', customerId);
      const navState = {
        customer_id: customerId,
        order_id: orderIdNummeric,
      };
      if (orderType =='2') {
        navigate('/add-delivery-address', { state: navState });
      } else {
        navigate('/payment-view?order_id='+orderIdNummeric, { state: navState });
      }
    }
  };

  const customFooterButtons = (
    <>
     
      <button  className="btn iconBtn btnLight"onClick={continueAsGuest}>
        Continue as Guest
      </button>
      <button className="btn iconBtn btnPrimary" onClick={saveRecords}>
        Next
      </button>
    </>
  );

  return (
    <MainLayout  guardedNavigate={guardedNavigate} footerButtons={customFooterButtons}>
      <div className="pageContent selectCustomerPage">
        <div className="container">
          <div className="d-flex justify-content-end mb-4 mb-lg-5">
            <a href="/order-review" className="btn iconBtn btnPrimary">
              <svg width="20" height="20" className="cartIcon">
                <use xlinkHref="./images/sprite.svg#iconCart"></use>
              </svg>
              <span>Cart</span>
            </a>
          </div>
          <div className="selectCustomerCol">
            <h4 className="lgTitle text-center pb-3">Select or Add A Customer</h4>
            <form className="formStyle">
              <div className="row g-3 g-lg-4">
                {/* Search box with autocomplete — separate from First Name below */}
                <div className="col-12 position-relative" ref={suggestionBoxRef}>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="text"
                      name="search"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="form-control"
                      placeholder={canSearchCustomers ? "Search by Name, Email, or Phone" : "Customer search is not available offline"}
                      disabled={!canSearchCustomers}
                      autoComplete="off"
                    />
                    {/* scan the customer's membership card (the camera scanner; hidden where there is none) */}
                    <ScanButton />
                  </div>

                  {suggestions.length > 0 && (
                    <ul className="autocomplete-list list-group position-absolute w-100 shadow-sm z-3">
                      {suggestions.map((suggestion, idx) => (
                        <li
                          key={idx}
                          onClick={() => handleSuggestionSelect(suggestion)}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: 'pointer' }}
                        >
                          {suggestion.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* First Name */}
                <div className="col-12">
                  <input
                    type="text"
                    name="first_name"
                    value={customer.first_name}
                    onChange={handleInputChange}
                    className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                    placeholder="First Name"
                  />
                  {errors.first_name && <p className="text-danger">{errors.first_name}</p>}
                </div>

                {/* Last Name */}
                <div className="col-12">
                  <input
                    type="text"
                    name="last_name"
                    value={customer.last_name}
                    onChange={handleInputChange}
                    className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                    placeholder="Last Name"
                  />
                  {errors.last_name && <p className="text-danger">{errors.last_name}</p>}
                </div>

                {/* Email */}
                <div className="col-12">
                  <input
                    type="email"
                    name="mail"
                    value={customer.mail}
                    onChange={handleInputChange}
                    className={`form-control ${errors.mail ? 'is-invalid' : ''}`}
                    placeholder="Email Address"
                  />
                  {errors.mail && <p className="text-danger">{errors.mail}</p>}
                </div>

                {/* Phone Number */}
                <div className="col-12">
                  <input
                    type="tel"
                    name="number"
                    value={customer.number}
                    onChange={handleInputChange}
                    className={`form-control ${errors.number ? 'is-invalid' : ''}`}
                    placeholder="Phone Number"
                  />
                  {errors.number && <p className="text-danger">{errors.number}</p>}
                </div>

                {/* Checkbox */}
                <div className="col-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="subscribed"
                      checked={customer.subscribed}
                      onChange={handleInputChange}
                      id="discountVouchers"
                    />
                    <label className="form-check-label" htmlFor="discountVouchers">
                      Receive discount vouchers & special promotions.
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <GuardedNavigationModal 
            isOpen={showModal} 
            onCancel={cancelNavigation} 
            onConfirm={confirmNavigation} 
        />
    </MainLayout>
  );
};

export default SelectCustomer;
